/**
 * Debug logger that captures logs and sends them to the worker
 * for remote viewing by Claude
 */

interface LogEntry {
  level: 'log' | 'warn' | 'error' | 'info' | 'group' | 'groupEnd';
  args: unknown[];
  timestamp: string;
}

class DebugLogger {
  private logs: LogEntry[] = [];
  private isCapturing = false;
  private originalConsole: Partial<typeof console> = {};

  /**
   * Start capturing console logs
   */
  startCapture(): void {
    if (this.isCapturing) return;
    this.isCapturing = true;
    this.logs = [];

    // Save original methods
    this.originalConsole = {
      log: console.log,
      warn: console.warn,
      error: console.error,
      info: console.info,
      group: console.group,
      groupEnd: console.groupEnd,
    };

    // Override console methods
    const levels: (keyof typeof this.originalConsole)[] = ['log', 'warn', 'error', 'info', 'group', 'groupEnd'];
    for (const level of levels) {
      const original = this.originalConsole[level];
      if (original) {
        (console as any)[level] = (...args: unknown[]) => {
          this.logs.push({
            level: level as LogEntry['level'],
            args: args.map(arg => this.serialize(arg)),
            timestamp: new Date().toISOString(),
          });
          (original as Function).apply(console, args);
        };
      }
    }

    console.info('🔴 Debug capture started');
  }

  /**
   * Stop capturing and restore original console
   */
  stopCapture(): void {
    if (!this.isCapturing) return;
    this.isCapturing = false;

    // Restore original methods
    for (const [level, fn] of Object.entries(this.originalConsole)) {
      if (fn) {
        (console as any)[level] = fn;
      }
    }

    console.info('⏹️ Debug capture stopped');
  }

  /**
   * Serialize values for JSON storage
   */
  private serialize(value: unknown): unknown {
    if (value === null || value === undefined) return value;
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return value;
    }
    if (value instanceof Error) {
      return { __error: true, message: value.message, stack: value.stack };
    }
    try {
      // Try to serialize, truncate if too large
      const json = JSON.stringify(value);
      if (json.length > 10000) {
        return { __truncated: true, preview: json.substring(0, 1000) + '...' };
      }
      return JSON.parse(json);
    } catch {
      return String(value);
    }
  }

  /**
   * Get captured logs
   */
  getLogs(): LogEntry[] {
    return this.logs;
  }

  /**
   * Clear captured logs
   */
  clearLogs(): void {
    this.logs = [];
  }

  /**
   * Send logs to the worker for remote viewing
   */
  async sendLogs(label?: string): Promise<void> {
    const payload = {
      label: label || 'debug',
      logCount: this.logs.length,
      logs: this.logs.slice(-100), // Last 100 entries
    };

    try {
      const response = await fetch(`${CONFIG.WORKER_BASE_URL}/debug/logs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        console.info(`✅ Sent ${this.logs.length} logs to debug endpoint`);
      } else {
        console.error('Failed to send logs:', await response.text());
      }
    } catch (error) {
      console.error('Error sending logs:', error);
    }
  }
}

// Global instance
export const debugLogger = new DebugLogger();

// Expose to window for easy access
declare global {
  interface Window {
    debugLogger: DebugLogger;
    startDebug: () => void;
    stopDebug: () => void;
    sendDebugLogs: (label?: string) => Promise<void>;
  }
}

window.debugLogger = debugLogger;
window.startDebug = () => debugLogger.startCapture();
window.stopDebug = () => debugLogger.stopCapture();
window.sendDebugLogs = (label?: string) => debugLogger.sendLogs(label);
